import type { AuthPort } from '../../domain/auth/AuthPort.js'
import type { SilentAuthResponse, SilentAuthOptions } from '../../utils/auth.js'
import type { LigonierAuthResponseDTO } from './LigonierAuthResponseDTO.js'
import { normalizeLigonierAuthResponse } from './normalizeLigonierAuthResponse.js'

export interface LigonierAuthAdapterConfig {
  authBaseUrl: string
}

/**
 * Ligonier implementation of AuthPort.
 *
 * This class owns ALL auth HTTP knowledge:
 *   - Silent auth check via session cookies
 *   - Logout API call
 *   - Access token storage
 *   - Response parsing
 *
 * The rest of the application never sees any of this.
 */
export class LigonierAuthAdapter implements AuthPort {
  constructor(private config: LigonierAuthAdapterConfig) { }

  private get authOrigin() {
    try {
      return new URL(this.config.authBaseUrl).origin
    } catch {
      throw new Error(`LigonierAuthAdapter: invalid authBaseUrl "${this.config.authBaseUrl}"`)
    }
  }

  async checkSilentAuth(options: SilentAuthOptions = {}): Promise<SilentAuthResponse> {
    const endpoint = options.endpoint ?? `${this.authOrigin}/api/silent`

    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        credentials: 'include',
        signal: options.abortSignal,
      })

      if (!response.ok) {
        // 401/403 mean the server explicitly rejected the session — clear any
        // stale token. 5xx errors are transient; preserve the token so a
        // temporary server outage doesn't force a re-login.
        if (response.status === 401 || response.status === 403) {
          this.clearToken()
        }
        return {
          authenticated: false,
          error: `Silent auth failed with status ${response.status}`,
        }
      }

      const dto: LigonierAuthResponseDTO = await response.json()
      const result = normalizeLigonierAuthResponse(dto)

      if (result.authenticated && result.accessToken) {
        this.storeToken(result.accessToken)
      } else {
        // Covers: not authenticated, OR authenticated but missing accessToken.
        // In both cases clear any stale token so downstream callers can't use it.
        this.clearToken()
      }

      return result
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw error
      }
      return {
        authenticated: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async logout(): Promise<void> {
    // Clear local tokens immediately — before any await — so localStorage is
    // clean by the time any auth-logout listeners run on the calling side.
    this.clearToken()
    try {
      await fetch(`${this.authOrigin}/logout`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch {
      // Ignore logout API errors — tokens are already cleared above
    }
  }

  private storeToken(accessToken: string): void {
    if (typeof window === 'undefined') return
    try {
      // Maintains the 'ligonier_auth_token' localStorage key that utils/auth.ts reads
      // via getStoredToken() for making authenticated API requests. This is an
      // intentional compatibility contract with the existing auth utility layer.
      localStorage.setItem('ligonier_auth_token', accessToken)
    } catch {
      // Ignore storage errors in restricted environments
    }
  }

  private clearToken(): void {
    if (typeof window === 'undefined') return
    try {
      localStorage.removeItem('ligonier_auth_token')
      // Also clear the refresh token written by utils/auth.ts storeTokens() so
      // that refreshAuthToken() cannot silently re-establish a session after logout.
      localStorage.removeItem('ligonier_refresh_token')
    } catch {
      // Ignore storage errors in restricted environments
    }
  }
}
